import { useEffect, useMemo, useRef, useState } from 'react'
import {
  fetchAssetLibrary, patchLibraryAsset, uploadAsset, uploadAssetVersion,
  type AssetLibraryResponse,
} from '../lib/api.js'
import { pillStyleFor } from '../lib/pills.js'

/**
 * Asset library (decisions 11 + 93) — the Wave9-curated attachment library:
 * case studies and product sheets the builder attaches to quotes, tagged by
 * product so the picker can auto-suggest them.
 *
 * Open to ALL Wave9 internal users (decision 93 — Mark's call, not admin-only).
 * PDF only, ≤ ~4.5MB (Netlify body cap). Retire, never delete: sent quotes pin
 * the version current at send (spec §10) and keep serving it forever — which
 * is also why "New version" never changes what an already-sent quote shows.
 */

export function AssetLibrary({ reseller = false }: {
  /** Decision 135: a RESELLER partner session — their own library + the global one read-only. */
  reseller?: boolean
}) {
  const [data, setData] = useState<AssetLibraryResponse | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  // Upload form
  const [file, setFile] = useState<File | null>(null)
  const [title, setTitle] = useState('')
  const [category, setCategory] = useState('')
  const [tagLines, setTagLines] = useState<string[]>([])
  const fileRef = useRef<HTMLInputElement>(null)

  // Per-asset edit state
  const [editing, setEditing] = useState<string | null>(null)
  const [editTitle, setEditTitle] = useState('')
  const [editCategory, setEditCategory] = useState('')
  const [editLines, setEditLines] = useState<string[]>([])
  const versionRefs = useRef<Record<string, HTMLInputElement | null>>({})

  const load = () => {
    fetchAssetLibrary().then(setData).catch((e: Error) => setError(e.message))
  }
  useEffect(load, [])

  const run = async (fn: () => Promise<unknown>) => {
    setError(null)
    setBusy(true)
    try { await fn(); load() } catch (e) { setError((e as Error).message) } finally { setBusy(false) }
  }

  const maxBytes = data?.maxBytes ?? 4_500_000
  const maxMb = (maxBytes / 1_000_000).toFixed(1)

  const productName = useMemo(() => {
    const m = new Map<string, string>()
    for (const p of data?.products ?? []) m.set(p.hsProductId, p.name)
    return m
  }, [data?.products])

  // Decision 99: tagging is by PRODUCT LINE — a short checkbox list, no search.
  const linePicker = (selected: string[], onChange: (lines: string[]) => void) => (
    <div className="rowflex" style={{ flexWrap: 'wrap', gap: 6 }}>
      {(data?.productLines ?? []).map((l) => (
        <label key={l} className={`chip chip--action ${selected.includes(l) ? 'chip--on' : ''}`} style={{ cursor: 'pointer' }}>
          <input type="checkbox" style={{ display: 'none' }} checked={selected.includes(l)}
            onChange={(e) => onChange(e.target.checked ? [...selected, l] : selected.filter((x) => x !== l))} />
          {l}
        </label>
      ))}
      {(data?.productLines ?? []).length === 0 && <span className="tiny muted">No Product Lines yet.</span>}
    </div>
  )

  const pickFile = (f: File | null) => {
    setFile(f)
    // Title defaults from the filename — what the picker shows should read
    // like a document, not a file system entry.
    if (f && !title.trim()) setTitle(f.name.replace(/\.pdf$/i, '').replace(/[-_]+/g, ' ').trim())
  }

  const fileProblem = (f: File | null): string | null => {
    if (!f) return null
    if (!/\.pdf$/i.test(f.name) && f.type !== 'application/pdf') return 'The library is PDF-only.'
    if (f.size > maxBytes) return `That file is ${(f.size / 1_000_000).toFixed(1)}MB — the cap is ${maxMb}MB.`
    return null
  }

  if (error && !data) return <p className="pill pill--stop">{error}</p>
  if (!data) return <p className="muted">Loading…</p>

  const problem = fileProblem(file)

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Asset library</h1>
          {reseller ? (
            <p>
              Your supporting documents alongside Wave9's — PDFs the builder attaches to your quotes.
              You manage your own; the Wave9 library is available read-only. PDF only, up to {maxMb}MB.
              Sent quotes keep the version current at send. Retire, never delete.
            </p>
          ) : (
            <p>
              Wave9's supporting documents — case studies and product sheets the builder attaches to quotes. PDF only, up to {maxMb}MB. Sent quotes keep the version current at send, so a new version never rewrites history. Retire, never delete.
            </p>
          )}
        </div>
      </div>

      {error && <div className="banner banner--stop"><strong>That didn't work.</strong><span>{error}</span></div>}

      <div className="card">
        <div className="card__head">
          <h2>{reseller ? 'Upload to your library' : 'Upload a new asset'}</h2>
          <span className="muted small">{reseller
            ? 'Visible and attachable on your quotes only — Wave9 staff can also see it.'
            : "It appears in every partner's attachment picker immediately."}</span>
        </div>
        <div className="card__body">
          <div className="rowflex" style={{ alignItems: 'flex-start' }}>
            <div className="field" style={{ width: 280 }}>
              <label>PDF file</label>
              <input ref={fileRef} className="input" type="file" accept="application/pdf,.pdf" style={{ width: '100%' }}
                onChange={(e) => pickFile(e.target.files?.[0] ?? null)} />
              {problem && <div className="pill pill--stop" style={{ marginTop: 6 }}>{problem}</div>}
              {file && !problem && <div className="hint">{(file.size / 1_000_000).toFixed(2)}MB</div>}
            </div>
            <div className="field" style={{ flex: 1, minWidth: 220 }}>
              <label>Title</label>
              <input className="input" style={{ width: '100%' }} placeholder="e.g. Fibre Circuit case study — Sheldon Trust"
                value={title} onChange={(e) => setTitle(e.target.value)} />
              <div className="hint">What the builder's picker shows.</div>
            </div>
            <div className="field" style={{ width: 180 }}>
              <label>Category <span className="muted">(optional)</span></label>
              <input className="input" style={{ width: '100%' }} placeholder="e.g. Case study"
                value={category} onChange={(e) => setCategory(e.target.value)} />
            </div>
          </div>
          <div className="field" style={{ marginTop: 8 }}>
            <label>Product Lines <span className="muted">(optional — the document auto-suggests on any quote carrying a line from these)</span></label>
            {linePicker(tagLines, setTagLines)}
          </div>
          <div className="rowflex" style={{ marginTop: 10 }}>
            <button className="btn btn--primary" disabled={busy || !file || !!problem || !title.trim()}
              onClick={() => run(async () => {
                await uploadAsset({ file: file!, title: title.trim(), category: category.trim() || undefined, productLines: tagLines })
                setFile(null); setTitle(''); setCategory(''); setTagLines([])
                if (fileRef.current) fileRef.current.value = ''
              })}>
              {busy ? 'Uploading…' : 'Upload to library'}
            </button>
          </div>
        </div>
      </div>

      <div className="card">
        <div className="card__head">
          <h2>Library — {data.assets.filter((a) => a.active).length} live{data.assets.some((a) => !a.active) ? `, ${data.assets.filter((a) => !a.active).length} retired` : ''}</h2>
          <span className="muted small">Downloads only ever reach customers via their quote link, pinned to the version sent.</span>
        </div>
        <div className="card__body">
          {data.assets.length === 0 && (
            <p className="muted small">
              Nothing yet. Upload the first case study or product sheet above — until then the builder's
              attachment picker says the library is empty.
            </p>
          )}
          {data.assets.map((a) => (
            <div key={a.id} className="school" style={{ flexDirection: 'column', alignItems: 'stretch' }}>
              <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                <div style={{ flex: 1 }}>
                  <strong>{a.title}</strong>{' '}
                  <span className="pill pill--brand">v{a.version}</span>{' '}
                  {!a.active && <span className="pill pill--stop">retired</span>}{' '}
                  {a.category && <span className="pill" style={pillStyleFor(a.category)}>{a.category}</span>}{' '}
                  {/* Decision 135: whose document. */}
                  {a.partnerId && <span className="pill pill--info tiny">{reseller ? 'yours' : (a.partnerName ?? 'partner')}</span>}
                  {!a.partnerId && reseller && <span className="pill tiny muted">Wave9</span>}
                  <div className="small muted">
                    {a.filename}
                    {a.sizeBytes != null && <> · {(a.sizeBytes / 1_000_000).toFixed(2)}MB</>}
                    {a.uploadedAt && <> · uploaded {new Date(a.uploadedAt).toLocaleDateString('en-GB')}</>}
                    {a.uploadedBy && <> by {a.uploadedBy}</>}
                    {a.attachedCount > 0 && <> · on {a.attachedCount} quote{a.attachedCount === 1 ? '' : 's'}</>}
                  </div>
                  {(a.productLines.length > 0 || a.hsProductIds.length > 0) && (
                    <div className="tiny muted" style={{ marginTop: 2 }}>
                      Suggested on: {[
                        ...a.productLines,
                        ...a.hsProductIds.map((id) => productName.get(id) ?? id),
                      ].join(', ')}
                    </div>
                  )}
                </div>
                <input type="file" accept="application/pdf,.pdf" style={{ display: 'none' }}
                  ref={(el) => { versionRefs.current[a.id] = el }}
                  onChange={(e) => {
                    const f = e.target.files?.[0] ?? null
                    e.target.value = ''
                    if (!f) return
                    const p = fileProblem(f)
                    if (p) { setError(p); return }
                    void run(() => uploadAssetVersion(a.id, f))
                  }} />
                {/* Decision 135: only rows this session may manage get actions —
                    a reseller sees the global library read-only. */}
                {a.editable && (
                  <>
                    <button className="btn btn--sm" disabled={busy} title="Sent quotes keep the version they were sent with — only new attachments pick this up."
                      onClick={() => versionRefs.current[a.id]?.click()}>New version</button>
                    <button className="btn btn--sm" disabled={busy} onClick={() => {
                      if (editing === a.id) { setEditing(null); return }
                      setEditing(a.id)
                      setEditTitle(a.title)
                      setEditCategory(a.category ?? '')
                      setEditLines(a.productLines)
                    }}>{editing === a.id ? 'Cancel' : 'Edit'}</button>
                    <button className={`btn btn--sm ${a.active ? 'btn--danger' : ''}`} disabled={busy}
                      onClick={() => run(() => patchLibraryAsset(a.id, { active: !a.active }))}>
                      {a.active ? 'Retire' : 'Reinstate'}
                    </button>
                  </>
                )}
              </div>
              {editing === a.id && (
                <div style={{ marginTop: 10, borderTop: '1px solid var(--line-soft)', paddingTop: 10 }}>
                  <div className="rowflex" style={{ alignItems: 'flex-start' }}>
                    <div className="field" style={{ flex: 1, minWidth: 200 }}>
                      <label>Title</label>
                      <input className="input" style={{ width: '100%' }} value={editTitle} onChange={(e) => setEditTitle(e.target.value)} />
                    </div>
                    <div className="field" style={{ width: 180 }}>
                      <label>Category</label>
                      <input className="input" style={{ width: '100%' }} value={editCategory} onChange={(e) => setEditCategory(e.target.value)} />
                    </div>
                  </div>
                  <div className="field" style={{ marginTop: 6 }}>
                    <label>Product Lines</label>
                    {linePicker(editLines, setEditLines)}
                  </div>
                  <div className="rowflex" style={{ marginTop: 8 }}>
                    <button className="btn btn--sm btn--primary" disabled={busy || !editTitle.trim()}
                      onClick={() => run(async () => {
                        await patchLibraryAsset(a.id, {
                          title: editTitle.trim(),
                          category: editCategory.trim() || null,
                          productLines: editLines,
                        })
                        setEditing(null)
                      })}>Save</button>
                    <span className="tiny muted">Title/category/tags only — the file itself changes via “New version”.</span>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </>
  )
}
